export interface RefNumber {
  number: number;
  page?: number;
}

export interface FileReference {
  file_id: string;
  file_name?: string;
  numbers: RefNumber[];
  primaryPage?: number;
  knowledgePath?: string;
  knowledgeAnchor?: string;
  isActivity?: boolean;
}

export interface ProcessedCitations {
  renumberedText: string;
  references: FileReference[];
}

export function processCitations(text: string | undefined): ProcessedCitations {
  if (!text) {
    return { renumberedText: '', references: [] };
  }
  const assigned = new Map<string, number>();
  const fileRefs = new Map<string, FileReference>();
  let counter = 0;
  let result = '';
  let i = 0;

  while (i < text.length) {
    const start = text.indexOf('[', i);
    if (start === -1) {
      result += text.slice(i);
      break;
    }
    const closeBracket = text.indexOf(']', start + 1);
    if (closeBracket === -1) {
      result += text.slice(i);
      break;
    }
    if (text[closeBracket + 1] !== '(') {
      result += text.slice(i, closeBracket + 1);
      i = closeBracket + 1;
      continue;
    }
    const closeParen = text.indexOf(')', closeBracket + 2);
    if (closeParen === -1) {
      result += text.slice(i, closeBracket + 1);
      i = closeBracket + 1;
      continue;
    }
    const numberToken = text.slice(start + 1, closeBracket).trim();
    const url = text.slice(closeBracket + 2, closeParen).trim();
    let handled = false;
    if (
      /^\d+$/.test(numberToken) &&
      url.startsWith('%7B') &&
      url.endsWith('%7D')
    ) {
      try {
        const decoded = decodeURIComponent(url).replace(/\+\s*/g, '');
        const parsed = JSON.parse(decoded);
        if (parsed && typeof parsed === 'object' && parsed.file_id) {
          const file_id = String(parsed.file_id);
          const page =
            typeof parsed.start_page === 'number'
              ? parsed.start_page
              : Array.isArray(parsed.pages) &&
                typeof parsed.pages[0] === 'number'
              ? parsed.pages[0]
              : undefined;
          const key = `${file_id}::${page ?? ''}`;
          const file_name =
            typeof parsed.file_name === 'string' && parsed.file_name
              ? parsed.file_name
              : undefined;

          let ref = fileRefs.get(file_id);
          if (!ref) {
            ref = { file_id, file_name, numbers: [], primaryPage: page };
            if (
              parsed.type === 'KNOWLEDGE' &&
              typeof parsed.path === 'string'
            ) {
              ref.knowledgePath = parsed.path;
              ref.knowledgeAnchor =
                typeof parsed.anchor === 'string' ? parsed.anchor : undefined;
            }
            if (parsed.type === 'ACTIVITY' || parsed.type === 'NOTE') {
              ref.isActivity = true;
            }
            fileRefs.set(file_id, ref);
          } else if (!ref.file_name && file_name) {
            ref.file_name = file_name;
          }

          let n = assigned.get(key);
          if (n === undefined) {
            counter += 1;
            n = counter;
            assigned.set(key, n);
            ref.numbers.push({ number: n, page });
          }

          result += text.slice(i, start);
          result += `[${n}](${url})`;
          i = closeParen + 1;
          handled = true;
        }
      } catch {
        // malformed citation JSON — leave the original markdown text in place
      }
    }
    if (!handled) {
      result += text.slice(i, closeParen + 1);
      i = closeParen + 1;
    }
  }

  return {
    renumberedText: result,
    references: Array.from(fileRefs.values()),
  };
}
